// Channel attribution for paid-ads CAC measurement.
//
// captureAttribution() runs once per tab at app bootstrap (main.tsx), BEFORE
// any client-side navigation can strip the query string. It persists to
// sessionStorage so the values survive React remounts and follow every
// /api/chat request in the tab. First-touch semantics are enforced both here
// (never overwrite an existing capture) and server-side (COALESCE in
// persistence.upsertConversation).

const STORAGE_KEY = 'tdee_attribution'

export interface Attribution {
  utm_source?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  referrer?: string
  gclid?: string
}

function clamp(value: string | null, max: number): string | undefined {
  const v = value?.trim()
  if (!v) return undefined
  return v.length > max ? v.slice(0, max) : v
}

export function captureAttribution(): void {
  try {
    if (sessionStorage.getItem(STORAGE_KEY)) return // first touch wins
    const params = new URLSearchParams(window.location.search)
    // Same-origin referrers are internal navigation, not a channel.
    const rawReferrer = document.referrer
    const externalReferrer =
      rawReferrer && !rawReferrer.startsWith(window.location.origin) ? rawReferrer : null
    const attribution: Attribution = {
      utm_source: clamp(params.get('utm_source'), 100),
      utm_campaign: clamp(params.get('utm_campaign'), 100),
      utm_term: clamp(params.get('utm_term'), 100),
      utm_content: clamp(params.get('utm_content'), 100),
      referrer: clamp(externalReferrer, 300),
      gclid: clamp(params.get('gclid'), 120),
    }
    if (
      !attribution.utm_source &&
      !attribution.utm_campaign &&
      !attribution.utm_term &&
      !attribution.utm_content &&
      !attribution.referrer &&
      !attribution.gclid
    ) {
      return // nothing to record; leave the key unset so a later tagged visit can capture
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution))
  } catch {
    // sessionStorage unavailable (private mode, quota) — attribution is best-effort
  }
}

export function readAttribution(): Attribution | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Attribution
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}
